import { useEffect, useState } from "react";
import styled from "styled-components";

import { AddressSuggestion } from "@hooks/user/useAddressSuggestions";
import { Autocomplete } from "@mui/material";
import { TextField } from "@runwayhealth/runway-components-react";

// `&&&` triples the selector specificity so these rules beat MUI's own
// compound `.MuiAutocomplete-listbox .MuiAutocomplete-option` rules no matter
// which stylesheet is injected last.
const AutoCompleteOption = styled.li`
  &&& {
    display: flex;
    align-items: center;
    justify-content: left;
    padding: 8px 16px;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  &&&:hover {
    background-color: rgba(0, 0, 0, 0.08);
  }

  &&&.Mui-focused {
    background-color: rgba(0, 0, 0, 0.12);
  }

  &&&[aria-selected="true"] {
    background-color: rgba(0, 0, 0, 0.14);
    color: #333333;
  }
`;

type Option = string | AddressSuggestion;

type AutoCompleteProps = {
  capturedVariant: React.Dispatch<React.SetStateAction<string | AddressSuggestion>>;
  options: Option[];
  // Replaces MUI's default substring filter. Pass an identity function when
  // `options` already come from a server-side search and must show verbatim.
  filterOptions?: (options: Option[]) => Option[];
  onFocus?: () => void;
  // Fires with the raw text on each keystroke (never on programmatic resets).
  onChange?: (value: string) => void;
  onKeyUp?: (value: React.SyntheticEvent) => void;
  defaultValue?: string;
  disabled?: boolean;
  handleBlur?: (event: React.FocusEvent<HTMLInputElement, Element>) => void;
  placeholder?: string;
  setSyncRequired?: React.Dispatch<React.SetStateAction<boolean>>;
  open: boolean;
};

const AutocompleteInput = ({
  onFocus,
  onChange,
  onKeyUp,
  handleBlur,
  capturedVariant,
  options,
  filterOptions,
  defaultValue,
  disabled = false,
  placeholder,
  open,
}: AutoCompleteProps) => {
  const [selectedOption, setSelectedOption] = useState<AddressSuggestion | null>(null);
  const [inputValue, setInputValue] = useState(defaultValue);

  useEffect(() => {
    setInputValue(defaultValue);
  }, [defaultValue]);

  const captureState = (value: AddressSuggestion | string | null) => {
    capturedVariant(value ?? "");

    if ((value as AddressSuggestion)?.street_line) {
      setSelectedOption(value as AddressSuggestion);
      setInputValue((value as AddressSuggestion).street_line);
    }
  };

  const handleInputChange = (
    event: React.SyntheticEvent,
    newInputValue: string,
    reason: string
  ) => {
    // Only update inputValue if the user is typing manually
    if (selectedOption && newInputValue === selectedOption.full_address) {
      // Avoid overwriting custom value with label
      return;
    }
    setInputValue(newInputValue); // Sync manual input change
    if (reason === "input") {
      onChange?.(newInputValue);
    }
  };

  return (
    <Autocomplete
      clearOnBlur={false}
      freeSolo
      open={open}
      value={selectedOption}
      onKeyUp={onKeyUp}
      onBlur={(event: React.FocusEvent<HTMLDivElement>) => {
        // Cast the event target to HTMLInputElement
        const inputElement = event.target as HTMLInputElement;
        captureState(inputElement.value);
      }}
      options={options}
      filterOptions={filterOptions}
      inputValue={inputValue ?? ""}
      onChange={(e, value) => {
        captureState(value);
      }}
      onInputChange={handleInputChange}
      defaultValue={defaultValue}
      getOptionLabel={(option) => (option as AddressSuggestion).full_address ?? option}
      renderInput={(params) => (
        <>
          <div ref={params.InputProps.ref}>
            <TextField
              data-testid="text-autocomplete"
              value={inputValue}
              autoComplete={"disabled"}
              {...params.inputProps}
              name="state"
              onFocus={onFocus}
              onBlur={(e: React.FocusEvent<HTMLInputElement, Element>) =>
                handleBlur ? handleBlur(e) : null
              }
              placeholder={placeholder}
              disabled={disabled}
            />
          </div>
        </>
      )}
      renderOption={(props, option: Option) => {
        // Keep MUI's own option class so the row inherits its block layout;
        // only append the PostHog masking class.
        return (
          <AutoCompleteOption
            {...props}
            className={`${props.className ?? ""} ph-no-capture`}
            data-testid={`autocomplete-element-${(option as AddressSuggestion).city ?? option}`}
          >
            {(option as AddressSuggestion).full_address ?? option}
          </AutoCompleteOption>
        );
      }}
    />
  );
};

export default AutocompleteInput;
